import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth, useLanguage, api } from '../context/AppContext';
import ProgressRing from './ProgressRing';
import { StreakFlame } from './StreakFlame';
import PushOnboardingModal from './PushOnboardingModal';
import ErrorBoundary from './ErrorBoundary';
import PoweredByYumix from './PoweredByYumix';
import RippleLink from './RippleLink';
import OldDomainMigrationBanner from './OldDomainMigrationBanner';
import InAppNotificationManager from './InAppNotificationManager';
import { 
  LayoutDashboard, 
  UtensilsCrossed, 
  Settings, 
  Users, 
  LogOut,
  Zap,
  BookOpen,
  Compass,
  CalendarDays,
  Globe,
  Facebook,
  ShoppingCart,
  Shield,
  MoreHorizontal,
  Leaf,
  BarChart3,
  Droplets,
  Trophy,
  Footprints,
  Moon,
  UserCircle,
  Heart,
  Award,
  ChefHat,
  Cpu,
  Dumbbell,
  Pill,
  MessageSquare,
  ArrowUpDown,
  Swords,
  FileText,
  ChevronDown,
  ChevronRight,
  Crosshair,
  Crown,
  Timer,
  Rocket,
  Activity
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { GlobalFAB } from './GlobalFAB';
import NotificationCenter from './NotificationCenter';
import { useAdaptiveUI } from '../hooks/useAdaptiveUI';
import { useFeatures } from '../hooks/useFeatures';

// ── Navigation categories ──
const buildNavCategories = (t, language, isPrivilegedUser, features = {}) => {
  const fr = language === 'fr';
  const hasMessaging = features.has_messaging !== false; // default true
  const hasGroceryDeals = features.has_grocery_deals !== false; // default true

  const personalItemsAll = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/meals', icon: UtensilsCrossed, label: t('nav.meals') },
    { path: '/coach-ai', icon: UserCircle, label: t('nav.coach') },
    { path: '/messaging', icon: MessageSquare, label: t('nav.messaging'), badge: 'messages', tierGated: 'messaging' },
    { path: '/hydration', icon: Droplets, label: t('nav.hydration') },
    { path: '/fasting', icon: Timer, label: fr ? 'Jeune' : 'Fasting' },
    { path: '/sleep', icon: Moon, label: fr ? 'Sommeil' : 'Sleep' },
    { path: '/workouts', icon: Dumbbell, label: t('nav.workouts') },
    // Iter. 406.p — Permanent CTA to activate the first 6-week cycle.
    // Hidden once an active cycle exists (gated via features.has_active_cycle).
    {
      path: '/dashboard?activate-cycle=1',
      icon: Rocket,
      label: fr ? 'Activer mon cycle 6 semaines' : 'Activate my 6-week cycle',
      tierGated: 'cycle_activation',
      testId: 'nav-activate-cycle',
    },
    { path: '/activities', icon: Activity, label: fr ? 'Activités' : 'Activities' },
    { path: '/power-register', icon: Trophy, label: fr ? 'Mes Records' : 'My Records' },
    { path: '/evolution', icon: ArrowUpDown, label: fr ? 'Mon Évolution' : 'My Evolution' },
    { path: '/victories', icon: Trophy, label: t('nav.badges') },
  ];
  const personalItems = personalItemsAll.filter(
    it => !(it.tierGated === 'messaging' && !hasMessaging)
      && !(it.tierGated === 'cycle_activation' && features.has_active_cycle === true),
  );

  const nutritionItemsAll = [
    { path: '/recipes', icon: BookOpen, label: fr ? 'Recettes IA' : 'AI Recipes' },
    { path: '/my-recipes', icon: ChefHat, label: fr ? 'Mes Recettes' : 'My Recipes' },
    { path: '/planner', icon: CalendarDays, label: fr ? 'Planning' : 'Planner' },
    { path: '/grocery-list', icon: ShoppingCart, label: fr ? 'Panier IA' : 'Smart Cart' },
    { path: '/deals', icon: ShoppingCart, label: fr ? 'Aubaines' : 'Deals', tierGated: 'deals' },
    { path: '/budget-hero', icon: Leaf, label: 'Budget Eco', tierGated: 'deals' },
    { path: '/supplements', icon: Pill, label: fr ? 'Supplements' : 'Supplements' },
  ];
  const nutritionItems = nutritionItemsAll.filter(
    it => !(it.tierGated === 'deals' && !hasGroceryDeals),
  );

  const socialItems = [
    { path: '/community', icon: Footprints, label: fr ? 'Communaute' : 'Community', testId: 'nav-community' },
    { path: '/steps-ranking', icon: Trophy, label: fr ? 'Extra Miles' : 'Extra Miles', testId: 'nav-steps-ranking' },
    { path: '/legends', icon: Crown, label: fr ? 'Mur des Legendes' : 'Wall of Legends' },
    { path: '/clash', icon: Swords, label: 'Clash' },
    { path: '/badges', icon: Award, label: 'Badges' },
    { path: '/recap', icon: Zap, label: fr ? 'Recap' : 'Recap' },
  ];

  const categories = [
    { key: 'personal', label: fr ? 'MON SUIVI' : 'MY TRACKING', items: personalItems, defaultOpen: true },
    { key: 'nutrition', label: 'NUTRITION', items: nutritionItems },
    { key: 'social', label: fr ? 'SOCIAL & DEFIS' : 'SOCIAL & CHALLENGES', items: socialItems },
  ];

  if (isPrivilegedUser) {
    categories.push({
      key: 'coach',
      label: fr ? 'QG COACH' : 'COACH HQ',
      icon: Crosshair,
      highlight: true,
      items: [
        { path: '/coach', icon: Crosshair, label: fr ? 'Centre de Commandement' : 'Command Center' },
        { path: '/analytics', icon: BarChart3, label: 'Analytics' },
        { path: '/class-census', icon: BarChart3, label: fr ? 'Recensement' : 'Census' },
        { path: '/monthly-report', icon: FileText, label: fr ? 'Rapport Mensuel' : 'Monthly Report' },
        { path: '/admin', icon: Shield, label: 'Admin' },
        { path: '/admin/algorithm-check', icon: Cpu, label: 'God Mode' },
        { path: '/admin/clinic', icon: Heart, label: fr ? 'Suivi Clinique' : 'Clinical Tracking' },
      ],
    });
  }

  categories.push({
    key: 'other',
    label: fr ? 'AUTRES' : 'OTHER',
    items: [
      { path: '/guide', icon: Compass, label: fr ? 'Guide YUMIX' : 'YUMIX Guide' },
      { path: '/settings', icon: Settings, label: t('nav.settings') },
    ],
  });

  return categories;
};

// ── Collapsible Nav Category ──
const NavCategory = ({ category, isActive, isEpure, unreadMessages, openCategories, toggleCategory }) => {
  const isOpen = openCategories.has(category.key);

  return (
    <div className="mb-1" data-testid={`nav-category-${category.key}`}>
      <button
        onClick={() => toggleCategory(category.key)}
        className={`w-full flex items-center justify-between px-3 py-2 rounded-md font-heading font-black uppercase tracking-[0.18em] transition-colors ${
          category.highlight
            ? 'text-primary hover:bg-primary/10'
            : 'text-white hover:text-white'
        }`}
        style={{ fontSize: '1.05rem' }}
        data-testid={`toggle-category-${category.key}`}
      >
        <span className="flex items-center gap-1.5">
          {category.icon && <category.icon className="w-3.5 h-3.5" />}
          {category.label}
        </span>
        {isOpen
          ? <ChevronDown className="w-3.5 h-3.5 opacity-60" />
          : <ChevronRight className="w-3.5 h-3.5 opacity-60" />
        }
      </button>

      {isOpen && (
        <div className="mt-0.5 space-y-0.5">
          {category.items.map((item) => {
            const active = isActive(item.path);
            const cyan = '#00FFFF';
            const baseShadow = '0 0 8px rgba(0, 255, 255, 0.6), 0 0 14px rgba(0, 255, 255, 0.3)';
            const activeShadow = '0 0 10px #00FFFF, 0 0 20px rgba(0, 255, 255, 0.7), 0 0 30px rgba(0, 255, 255, 0.4)';
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={item.testId || `nav-${item.path.replace(/\//g, '-').slice(1)}`}
                className={`relative flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-200 ${isEpure ? 'text-base py-3' : 'text-sm'} ${
                  active ? 'elite-active-row' : 'hover:bg-white/5'
                }`}
              >
                <div className="relative">
                  <item.icon
                    className={`${isEpure ? 'w-6 h-6' : 'w-4 h-4'}`}
                    style={{
                      color: cyan,
                      filter: active
                        ? 'drop-shadow(0 0 8px rgba(0, 255, 255, 0.9))'
                        : 'drop-shadow(0 0 5px rgba(0, 255, 255, 0.6))',
                      transition: 'filter 200ms',
                    }}
                  />
                  {item.badge === 'messages' && unreadMessages > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1" data-testid="unread-badge-desktop">
                      {unreadMessages > 9 ? '9+' : unreadMessages}
                    </span>
                  )}
                </div>
                <span
                  className="font-medium"
                  style={{
                    color: cyan,
                    textShadow: active ? activeShadow : baseShadow,
                    transition: 'text-shadow 200ms',
                  }}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const { features } = useFeatures();
  const location = useLocation();
  const [weeklyProgress, setWeeklyProgress] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [workoutStreak, setWorkoutStreak] = useState(0);
  const { isEpure, isJunior } = useAdaptiveUI();

  // Track which categories are open
  const [openCategories, setOpenCategories] = useState(new Set(['personal']));

  // Auto-open the category containing the active route
  useEffect(() => {
    const categories = buildNavCategories(t, language, isPrivilegedUser, features);
    for (const cat of categories) {
      if (cat.items.some(item => location.pathname === item.path || location.pathname.startsWith(item.path + '/'))) {
        setOpenCategories(prev => {
          if (prev.has(cat.key)) return prev;
          const next = new Set(prev);
          next.add(cat.key);
          return next;
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleCategory = (key) => {
    setOpenCategories(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  
  useEffect(() => {
    if (!user) return;
    api.get('/dashboard/stats').then(res => {
      const stats = res.data;
      const meals = stats?.weekly_meals_logged || 0;
      const target = stats?.weekly_meals_target || 21;
      setWeeklyProgress(Math.min(100, Math.round((meals / target) * 100)));
    }).catch(() => {});
    // Fetch workout streak
    api.get('/workouts/streak').then(res => {
      setWorkoutStreak(res.data.streak || 0);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const fetchUnread = () => {
      api.get('/messages/unread-count').then(res => {
        setUnreadMessages(res.data.unread_count || 0);
      }).catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [user]);
  
  const culturalMode = user?.cultural_mode || 'standard';
  const clinicalProfile = user?.clinical_mode?.enabled ? (user.clinical_mode.condition_types || [])[0] : null;
  const activeModeLabel = (() => {
    if (clinicalProfile && clinicalProfile !== 'standard') {
      const m = { neuro_support: 'Neuro', oncology: 'Onco', cardiovascular: 'Cardio', bariatric: 'Baria' };
      return m[clinicalProfile] || null;
    }
    if (culturalMode !== 'standard') {
      const m = { ramadan: 'Ramadan', halal: 'Halal', kasher: 'Kasher', careme: 'Careme', hindou: 'Hindou' };
      return m[culturalMode] || null;
    }
    return null;
  })();
  
  const isPrivilegedUser = user?.role === 'coach' || user?.role === 'admin' || user?.role === 'super_admin';

  const categories = buildNavCategories(t, language, isPrivilegedUser, features);
  
  // Flat list for mobile (keep existing behavior)
  const allNavItems = categories.flatMap(c => c.items);

  // Epure mode filtering
  const EPURE_PATHS = ['/dashboard', '/coach-ai', '/meals', '/hydration', '/fasting', '/workouts', '/activities', '/evolution', '/deals', '/settings', '/recipes', '/my-recipes', '/grocery-list', '/planner', '/messaging', '/supplements', '/leaderboard', '/community', '/steps-ranking'];
  const displayCategories = (isEpure && !isPrivilegedUser)
    ? categories.map(c => ({ ...c, items: c.items.filter(i => EPURE_PATHS.includes(i.path)) })).filter(c => c.items.length > 0)
    : categories;

  const displayNavItems = (isEpure && !isPrivilegedUser)
    ? allNavItems.filter(item => EPURE_PATHS.includes(item.path))
    : allNavItems;
  
  const isActive = (path) => location.pathname === path;

  return (
    <div className={`min-h-screen bg-background ${isEpure ? 'epure-mode' : ''} ${isJunior ? 'junior-mode' : ''}`}>
      <div className="noise-overlay" />
      <ErrorBoundary silent label="push-onboarding-modal">
        <PushOnboardingModal />
      </ErrorBoundary>
      
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 h-screen w-64 flex-col border-r border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-40">
        {/* Logo — Yuri spec 2026-02 (cockpit phase): subtle silver pulsing
            glow so the brand acts as the "power-on" indicator of the
            command center. Keyframes defined in /src/index.css. */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center w-10 h-10 rounded overflow-hidden"
              style={{ animation: 'yumix-cockpit-pulse 3.6s ease-in-out infinite' }}
            >
              <img src="/icons/yumix_logo_sidebar.png" alt="YUMIX" className="w-10 h-10 object-contain" />
            </div>
            <span
              className="font-heading text-2xl font-black uppercase tracking-tight text-foreground"
              style={{ animation: 'yumix-text-pulse 3.6s ease-in-out infinite' }}
            >
              YUMIX
            </span>
            {workoutStreak > 0 && (
              <span className="flex items-center gap-1" data-testid="header-streak">
                <StreakFlame streak={workoutStreak} size="sm" />
                <span className="text-xs font-bold text-orange-400">{workoutStreak}</span>
              </span>
            )}
          </div>
          <NotificationCenter />
        </div>
        {activeModeLabel && (
          <div className="px-4 py-2 border-b border-border" data-testid="mode-badge-desktop">
            <div className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-primary/15 border border-primary/30 rounded-full">
              <span className="text-xs font-bold text-primary tracking-wide">{activeModeLabel}</span>
            </div>
          </div>
        )}
        
        {/* Categorized Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
          {displayCategories.map((category) => (
            <NavCategory
              key={category.key}
              category={category}
              isActive={isActive}
              isEpure={isEpure}
              unreadMessages={unreadMessages}
              openCategories={openCategories}
              toggleCategory={toggleCategory}
            />
          ))}
        </nav>
        
        {/* User Profile */}
        <div className="px-4 py-3 border-t border-border">
          <div className="flex flex-col gap-1 mb-2">
            <a
              href="https://www.facebook.com/groups/753188122904936/?ref=share&mibextid=NSMWBT"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 py-1.5 rounded-lg text-muted-foreground hover:bg-[#1877F2]/10 hover:text-[#1877F2] transition-all duration-200"
              data-testid="facebook-link"
            >
              <Facebook className="w-4 h-4" />
              <span className="font-medium text-xs">Groupe Facebook</span>
            </a>
            <a
              href="https://www.extramiles.ca"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 py-1.5 rounded-lg text-muted-foreground hover:bg-secondary hover:text-primary transition-all duration-200"
              data-testid="website-link"
            >
              <Globe className="w-4 h-4" />
              <span className="font-medium text-xs">extramiles.ca</span>
            </a>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start gap-3 px-3 py-2.5 h-auto">
                <ProgressRing progress={weeklyProgress} size={38} strokeWidth={2.5}>
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={user?.custom_avatar ? `${process.env.REACT_APP_BACKEND_URL}${user.custom_avatar}` : user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </ProgressRing>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {/* Language switcher — 3 options. Active language has a checkmark. */}
              <DropdownMenuItem
                onClick={() => changeLanguage('fr')}
                data-testid="nav-lang-fr"
                className={language === 'fr' ? 'font-semibold' : ''}
              >
                🇫🇷 Français {language === 'fr' && '✓'}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => changeLanguage('en')}
                data-testid="nav-lang-en"
                className={language === 'en' ? 'font-semibold' : ''}
              >
                🇬🇧 English {language === 'en' && '✓'}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => changeLanguage('es')}
                data-testid="nav-lang-es"
                className={language === 'es' ? 'font-semibold' : ''}
              >
                🇪🇸 Español {language === 'es' && '✓'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                {t('nav.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Powered by YUMIX — silver pulse signature footer */}
        <PoweredByYumix variant="mini" className="mt-2 mb-1" />
      </aside>
      
      {/* Main Content */}
      <main className="md:ml-64 min-h-screen md:pb-8" style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'calc(120px + env(safe-area-inset-bottom, 0px))' }}>
        <OldDomainMigrationBanner />
        <InAppNotificationManager />
        <div key={location.pathname} className="elite-page-fade">
          {children}
        </div>
      </main>
      
      <GlobalFAB />
      
      {/* Mobile Bottom Nav */}
      <MobileNav 
        navItems={displayNavItems} 
        categories={displayCategories}
        isActive={isActive} 
        logout={logout} 
        t={t} 
        activeModeLabel={activeModeLabel} 
        isEpure={isEpure} 
        unreadMessages={unreadMessages}
        isPrivilegedUser={isPrivilegedUser}
      />
    </div>
  );
};

const MobileNav = ({ navItems, categories, isActive, logout, t, activeModeLabel, isEpure = false, unreadMessages = 0, isPrivilegedUser = false }) => {
  const [showMore, setShowMore] = useState(false);
  const location = useLocation();

  // Bottom-bar pill — measured math centering (Iter. 331 chirurgical fix)
  const navInnerRef = useRef(null);
  const itemRefs = useRef({}); // { [path|'__more__']: HTMLElement }
  const [pillStyle, setPillStyle] = useState({ left: 0, width: 40, opacity: 0 });

  // Mobile main tabs: Dashboard, Meals, Coach YUMIX, + QG Coach (if privileged), + More
  const mobileMainPaths = isPrivilegedUser
    ? ['/dashboard', '/meals', '/coach-ai', '/coach']
    : ['/dashboard', '/meals', '/coach-ai', '/messaging'];

  const mobileMain = mobileMainPaths
    .map(p => navItems.find(i => i.path === p))
    .filter(Boolean);

  // Override label for /coach on mobile
  const mobileMainDisplay = mobileMain.map(item => {
    if (item.path === '/coach') return { ...item, label: 'Espace Coach', icon: Crosshair };
    return item;
  });

  const mobileExtra = navItems.filter(i => !mobileMainPaths.includes(i.path));

  // Recompute pill X-position on route change / drawer toggle / resize.
  // Math: pill center = cell center. left = cellCenterX − PILL_WIDTH/2.
  useLayoutEffect(() => {
    const compute = () => {
      const PILL_WIDTH = 60; // Yuri spec Iter. 333 — 60px super-wide
      const navEl = navInnerRef.current;
      if (!navEl) return;
      let activeKey = null;
      if (showMore) {
        activeKey = '__more__';
      } else {
        for (const item of mobileMainDisplay) {
          if (location.pathname === item.path || location.pathname.startsWith(item.path + '/')) {
            activeKey = item.path;
            break;
          }
        }
      }
      const target = activeKey ? itemRefs.current[activeKey] : null;
      if (!target) {
        setPillStyle((s) => ({ ...s, opacity: 0 }));
        return;
      }
      const navRect = navEl.getBoundingClientRect();
      const itemRect = target.getBoundingClientRect();
      const cellCenterX = (itemRect.left - navRect.left) + itemRect.width / 2;
      const left = Math.round(cellCenterX - PILL_WIDTH / 2);
      setPillStyle({ left, width: PILL_WIDTH, opacity: 1 });
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, showMore]);

  return (
    <>
      {showMore && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setShowMore(false)}
        />
      )}

      {showMore && mobileExtra.length > 0 && (
        <div
          className="md:hidden fixed left-2 right-2 bg-black/95 border border-cyan-400/30 rounded-xl p-3 z-50 shadow-xl max-h-[70vh] overflow-y-auto"
          style={{
            bottom: 'calc(72px + env(safe-area-inset-bottom, 8px))',
            boxShadow: '0 0 32px rgba(0,255,255,0.15), inset 0 0 0 1px rgba(0,255,255,0.08)',
          }}
          data-testid="more-menu-drawer"
        >
          {categories.map(cat => {
            // Iter. 411.i — Youri: "Il manque un lien pour MES REPAS" dans
            // le menu Plus. On excluait `/meals` car il est déjà dans la
            // barre inférieure, mais les clients ne pensent pas à y aller
            // depuis les sous-pages. On garde donc /meals ET /dashboard
            // visibles ici comme redondance intentionnelle.
            const KEEP_IN_DRAWER = new Set(['/meals', '/dashboard']);
            const catItems = cat.items.filter(
              i => KEEP_IN_DRAWER.has(i.path) || !mobileMainPaths.includes(i.path)
            );
            if (catItems.length === 0) return null;
            return (
              <div key={cat.key} className="mb-3">
                <p
                  className="font-heading font-black uppercase tracking-[0.18em] text-white px-3 pt-2 pb-1.5"
                  style={{ fontSize: '1.05rem' }}
                >
                  {cat.label}
                </p>
                {catItems.map((item) => {
                  const active = isActive(item.path);
                  const cyan = '#00FFFF';
                  const baseShadow = '0 0 8px rgba(0, 255, 255, 0.6), 0 0 14px rgba(0, 255, 255, 0.3)';
                  const activeShadow = '0 0 10px #00FFFF, 0 0 20px rgba(0, 255, 255, 0.7), 0 0 30px rgba(0, 255, 255, 0.4)';
                  return (
                    <RippleLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setShowMore(false)}
                      data-testid={`more-nav-${item.path.replace(/\//g, '-').slice(1)}`}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all ${
                        active ? 'elite-active-row' : 'hover:bg-white/5'
                      }`}
                    >
                      <div className="relative">
                        <item.icon
                          className="w-4 h-4"
                          style={{
                            color: cyan,
                            filter: active
                              ? 'drop-shadow(0 0 8px rgba(0, 255, 255, 0.9))'
                              : 'drop-shadow(0 0 5px rgba(0, 255, 255, 0.6))',
                            transition: 'filter 200ms',
                          }}
                        />
                        {item.badge === 'messages' && unreadMessages > 0 && (
                          <span className="absolute -top-1 -right-1.5 bg-destructive text-destructive-foreground text-[8px] font-bold rounded-full min-w-[14px] h-3.5 flex items-center justify-center px-0.5">
                            {unreadMessages > 9 ? '9+' : unreadMessages}
                          </span>
                        )}
                      </div>
                      <span
                        className="text-sm font-medium"
                        style={{
                          color: cyan,
                          textShadow: active ? activeShadow : baseShadow,
                          transition: 'text-shadow 200ms',
                        }}
                      >
                        {item.label}
                      </span>
                    </RippleLink>
                  );
                })}
              </div>
            );
          })}
          <button
            onClick={() => { setShowMore(false); logout(); }}
            className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-destructive hover:bg-destructive/10 w-full mt-1"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm font-medium">{t('nav.logout')}</span>
          </button>
        </div>
      )}

      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 border-t border-border bg-background/95 backdrop-blur z-50 mobile-bottom-nav"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 8px)',
          // Hardware-accelerated + pinned. Fixes Android Chrome URL-bar glitch
          // and iOS Safari where fixed can drift when a parent gets transformed.
          transform: 'translateZ(0)',
          WebkitTransform: 'translateZ(0)',
          willChange: 'transform',
          contain: 'layout style',
        }}
      >
        <div
          ref={navInnerRef}
          className="relative grid items-end p-1.5 px-1"
          style={{
            gridTemplateColumns: `repeat(${mobileMainDisplay.length + 2}, minmax(0, 1fr))`,
          }}
        >
          {/* THE single pill — math-centered on the active cell via measured DOM */}
          <motion.span
            className="absolute h-1 rounded-full pointer-events-none"
            initial={false}
            animate={{
              left: pillStyle.left,
              width: pillStyle.width,
              opacity: pillStyle.opacity,
            }}
            transition={{ type: 'spring', stiffness: 500, damping: 32 }}
            style={{
              bottom: '2px',
              background: '#F97316',
              boxShadow: '0 0 15px #F97316, 0 0 25px #F97316',
              willChange: 'left, width',
            }}
            aria-hidden="true"
            data-testid="bottom-nav-active-pill"
          />

          {mobileMainDisplay.map((item) => {
            const shortLabel = item.path === '/dashboard' ? (item.label === 'Tableau de bord' ? 'Accueil' : 'Home')
              : item.path === '/coach-ai' ? 'Assistant IA'
              : item.path === '/coach' ? 'Espace Coach'
              : item.label;
            const active = isActive(item.path);
            return (
              <RippleLink
                key={item.path}
                to={item.path}
                ref={(el) => { itemRefs.current[item.path] = el; }}
                data-testid={`mobile-nav-${item.path.replace(/\//g, '-').slice(1)}`}
                className="flex flex-col items-center justify-end gap-0.5 min-w-0 pt-3 pb-1.5 rounded-lg transition-colors duration-200"
              >
                <div className="relative">
                  {/* Iter. 406.fr.5 — Premium icon-island when active */}
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 -m-1.5 rounded-xl bg-gradient-to-br from-orange-500/30 to-amber-700/15 border border-orange-400/30 transition-opacity duration-200 pointer-events-none"
                    style={{ opacity: active ? 1 : 0 }}
                  />
                  <item.icon
                    className="w-5 h-5 relative"
                    style={{
                      color: active ? '#FFE4B5' : '#94A3B8',
                      filter: active ? 'drop-shadow(0 0 6px rgba(249,115,22,0.7))' : 'none',
                      transition: 'color 200ms, filter 200ms',
                    }}
                  />
                  {item.badge === 'messages' && unreadMessages > 0 && (
                    <span className="absolute -top-1 -right-2 bg-destructive text-destructive-foreground text-[8px] font-bold rounded-full min-w-[14px] h-3.5 flex items-center justify-center px-0.5 z-10" data-testid="unread-badge-mobile">
                      {unreadMessages > 9 ? '9+' : unreadMessages}
                    </span>
                  )}
                </div>
                <span
                  className="text-[9px] font-medium truncate max-w-full mt-1"
                  style={{ color: active ? '#FFFFFF' : '#94A3B8' }}
                >
                  {shortLabel}
                </span>
              </RippleLink>
            );
          })}
          <div className="flex flex-col items-center justify-end gap-0.5 py-1.5">
            <NotificationCenter />
          </div>
          <button
            onClick={() => setShowMore(!showMore)}
            ref={(el) => { itemRefs.current.__more__ = el; }}
            data-testid="mobile-nav-more"
            className="flex flex-col items-center justify-end gap-0.5 min-w-0 pt-3 pb-1.5 rounded-lg transition-colors duration-200"
          >
            <MoreHorizontal
              className="w-5 h-5"
              style={{
                color: showMore ? '#FFFFFF' : '#94A3B8',
                filter: showMore ? 'drop-shadow(0 -3px 10px rgba(249,115,22,0.85))' : 'none',
                transition: 'color 200ms, filter 200ms',
              }}
            />
            <span className="text-[9px] font-medium" style={{ color: showMore ? '#FFFFFF' : '#94A3B8' }}>
              Plus
            </span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default Layout;
